import { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Currency,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppDispatch } from '@store';
import { Fintrac, FintracTransaction } from '../types';
import {
  useCancelReportMutation,
  useDeleteFintracReportMutation,
  useDeselectAllTransactionInReportMutation,
  useLazyGetFintracReportQuery,
  useLazyGetFintracReportTransactionsQuery,
  useSelectAllTransactionInReportMutation,
  useSelectTransactionInReportMutation,
  useSubmitReportMutation,
} from '../store/services';
import formatFintracTransactions from '../helpers/format-fintrac-transactions';
import { setSelectedTransactions } from '../store/slices/report';
import { setNotificationVisible } from '../../../../../../store/slice';

import ROUTES from '../../../routes';

const useDetails = ({
  reportId,
  transactions,
  currencies,
  activePermissions,
  report,
}: {
  reportId?: string;
  transactions?: FintracTransaction[];
  currencies?: Currency[];
  activePermissions?: PermissionEnum[];
  report?: Fintrac;
}) => {
  const [cancelReportVisible, setCancelReportVisible] = useState(false);
  const [submitReportVisible, setSubmitReportVisible] = useState(false);
  const [addTransactionsSidepanelVisible, setAddTransactionsSidepanelVisible] =
    useState(false);
  const [selectedReportTransferDate, setSelectedReportTransferDate] =
    useState('');
  const [selectedTransactionId, setSelectedTransactionId] = useState('');
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [reportType, setReportType] = useState('');

  const { state } = useLocation();

  const [getReport, { isLoading, isFetching }] = useLazyGetFintracReportQuery();
  const [
    getFintracReportTransactions,
    { isLoading: transactionsIsLoading, isFetching: transactionsIsFetching },
  ] = useLazyGetFintracReportTransactionsQuery();
  const [
    selectTransactionInReport,
    { isLoading: requestInProgress, isSuccess },
  ] = useSelectTransactionInReportMutation();
  const [deselectAllTransactions, { isLoading: deselectAllInProgress }] =
    useDeselectAllTransactionInReportMutation();
  const [
    selectAllTransactions,
    { isLoading: selectAllInProgress, isSuccess: selectAllIsSuccess },
  ] = useSelectAllTransactionInReportMutation();
  const [
    deleteFintracReport,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useDeleteFintracReportMutation();
  const [submitReport, { isLoading: submitInProgress }] =
    useSubmitReportMutation();
  const [cancelReport, { isLoading: cancelInProgress }] =
    useCancelReportMutation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onRowsSelect = async (ids: string[], id?: string) => {
    dispatch(setSelectedTransactions(ids));

    if (id) {
      setSelectedTransactionId(id);

      await selectTransactionInReport({
        id,
        isSelectedForReport: !!ids?.includes(id),
      });
    }
  };

  const formattedData = useMemo(() => {
    const data = formatFintracTransactions(
      transactions as FintracTransaction[],
      currencies,
    );

    return data;
  }, [transactions, currencies]);

  const onCheckStatus = async () => {
    await getReport(reportId as string);
  };

  const onSubmitReport = async () => {
    setDeleteError('');

    const response = (await submitReport(reportId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    } else {
      setSubmitReportVisible(false);

      dispatch(
        setNotificationVisible({
          visible: true,
          text: 'Report was submited',
          type: '',
          loading: false,
        }),
      );

      setTimeout(() => {
        getReport(reportId as string);
      }, 1500);

      setTimeout(() => {
        dispatch(
          setNotificationVisible({
            visible: false,
            text: '',
            type: '',
          }),
        );
      }, 3000);
    }
  };

  const onCancelReport = async () => {
    setDeleteError('');

    const response = (await cancelReport(reportId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    } else {
      setCancelReportVisible(false);

      dispatch(
        setNotificationVisible({
          visible: true,
          text: 'Report has been canceled',
          type: '',
          loading: false,
        }),
      );

      setTimeout(() => {
        getReport(reportId as string);
      }, 500);

      setTimeout(() => {
        dispatch(
          setNotificationVisible({
            visible: false,
            text: '',
            type: '',
          }),
        );
      }, 3000);
    }
  };

  const onDeleteReport = async () => {
    setDeleteError('');

    const response = (await deleteFintracReport(reportId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    }
  };

  const onToggleAll = async (toggle: boolean) => {
    if (!toggle) {
      await deselectAllTransactions(reportId as string);
    } else {
      await selectAllTransactions(reportId as string);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      setSelectedTransactionId('');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (selectAllIsSuccess && reportId) {
      getReport(reportId);
    }
  }, [selectAllIsSuccess, reportId]);

  useEffect(() => {
    if (deleteIsSuccess) {
      navigate(
        reportType === 'eftr' ? ROUTES.FINTRAC_EFTR : ROUTES.FINTRAC_LVCTR,
      );
    }
  }, [deleteIsSuccess, reportType]);

  useEffect(() => {
    if (state?.type) {
      setReportType(state?.type);
    } else if (!state?.type && report) {
      setReportType(report?.reportType?.toLowerCase());
    }
  }, [state, report]);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.REGULATORY_REPORT_FINTRAC_LIST,
      ) &&
      reportId
    ) {
      getReport(reportId);
      getFintracReportTransactions(`?regulatoryReportId=${reportId}&take=30`);
    }
  }, [activePermissions, reportId]);

  return {
    onRowsSelect,
    formattedData,
    isLoading,
    isFetching,
    cancelReportVisible,
    setCancelReportVisible,
    submitReportVisible,
    setSubmitReportVisible,
    addTransactionsSidepanelVisible,
    setAddTransactionsSidepanelVisible,
    transactionsIsLoading,
    transactionsIsFetching,
    requestInProgress,
    selectedTransactionId,
    onCheckStatus,
    setDeletePopupVisible,
    deletePopupVisible,
    deleteError,
    deleteInProgress,
    onDeleteReport,
    onSubmitReport,
    submitInProgress,
    onCancelReport,
    cancelInProgress,
    selectAllInProgress,
    deselectAllInProgress,
    selectedReportTransferDate,
    setSelectedReportTransferDate,
    onToggleAll,
    reportType,
  };
};

export default useDetails;
